import { ElementType, FunctionComponent, memo, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import sha1 from 'sha1';
import { UserContext } from '../../context';
import { StoryCardStatus, StoryCardType } from '../../services';
import {
  DetailsContentProps,
  FeedCardPlaceholder,
  getStoryCardName,
  getStoryCardTitle,
  useAnalytics,
  useAppDispatch,
  useAppSelector,
  useDeepLink,
} from '../../shared';
import { getCard } from '../../slices';
import { NoContent } from '../Error';

import classes from './CardDetails.module.scss';

const CARD_TYPES_WITH_TITLE_HEADING = [
  StoryCardType.AUDIO,
  StoryCardType.EDITORIAL,
  StoryCardType.IMAGE,
  StoryCardType.PDF,
  StoryCardType.POLL,
  StoryCardType.VIDEO,
];

interface CardDetailsProps {
  ContentComponent: ElementType<DetailsContentProps>;
}

export const CardDetails: FunctionComponent<CardDetailsProps> = memo(({ ContentComponent }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { logClickOpenArticle } = useAnalytics();

  const { isDraft } = useDeepLink();

  const { card, isFetched } = useAppSelector(({ cardDetails }) => cardDetails);

  const { id: userId, channelId } = useContext(UserContext).userInfo.userData;

  const [searchParams] = useSearchParams();

  const storyId = Number(useParams().storyId);

  const cardId = Number(useParams().cardId);

  const scrollToComments = searchParams.get('scrollToComments') === 'true';

  const scrollToTags = searchParams.get('scrollToTags') === 'true';

  const activeCommentsInput = searchParams.get('activeCommentsInput') === 'true';

  useEffect(() => {
    logClickOpenArticle({
      user_Id: sha1(`${userId}`),
      channel_id: channelId,
      story_id: storyId,
      item_id: cardId,
      time_stamp: new Date().toISOString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logClickOpenArticle]);

  useEffect(() => {
    dispatch(getCard({ storyId, cardId }));
  }, [cardId, dispatch, storyId]);

  const renderTitle = useMemo(() => {
    if (
      !card ||
      (CARD_TYPES_WITH_TITLE_HEADING.includes(card.type) && getStoryCardTitle(card)?.trim())
    ) {
      return null;
    }

    return <h1 className={classes['card-details__title']}>{getStoryCardName({ card, t })}</h1>;
  }, [card, t]);

  if (!isFetched) {
    return <FeedCardPlaceholder />;
  }

  if (!card) {
    return <NoContent />;
  }

  if (card.status === StoryCardStatus.PUBLISHED || isDraft) {
    return (
      <>
        {renderTitle}
        <ContentComponent
          card={card}
          scrollToComments={scrollToComments}
          scrollToTags={scrollToTags}
          activeCommentsInput={activeCommentsInput}
          isPageView
        />
      </>
    );
  }

  return <NoContent />;
});
